import type { ConferenceContest, ConferenceVotingRound, InformationPage, LocalString, VotingRoundItem } from "@/types";
import { orderContests } from "@/entrypoints/shared/contest_utilities";

export type OrderedRoundItem =
  | { type: "Contest", reference: string, contest: ConferenceContest }
  | { type: "InformationPage", reference: string, infoPage: InformationPage };

// A voting round's contests and information pages in round order.
// Rounds without items (e.g. contests attached via contest_ids) fall back to their contests.
export function orderedRoundItems(
  votingRound: ConferenceVotingRound | undefined,
  votingRoundItems: VotingRoundItem[],
  contests: ConferenceContest[],
  informationPages: InformationPage[],
): OrderedRoundItem[] {
  if (!votingRound) return [];

  const roundItems = votingRoundItems
    .filter(item => item.votingRoundReference === votingRound.reference)
    .sort((a, b) => a.position - b.position);

  if (roundItems.length === 0) {
    const roundContests = contests.filter(contest => votingRound.contestReferences.includes(contest.reference));
    return orderContests(roundContests, votingRound.contestPositions)
      .map((contest): OrderedRoundItem => ({ type: "Contest", reference: contest.reference, contest }));
  }

  return roundItems.flatMap((item): OrderedRoundItem[] => {
    if (item.itemType === "Contest") {
      const contest = contests.find(c => c.reference === item.itemReference);
      return contest ? [{ type: "Contest", reference: contest.reference, contest }] : [];
    }
    const infoPage = informationPages.find(p => p.reference === item.itemReference);
    return infoPage ? [{ type: "InformationPage", reference: infoPage.reference, infoPage }] : [];
  });
}

// Pages are only shown to the voter groups they are assigned to
export function informationPageVisibleTo(page: InformationPage, voterGroupReference: string): boolean {
  return (page.voterGroupReferences || []).includes(voterGroupReference);
}

export function localizedValue(value: LocalString | undefined, locale: string, fallbackLocale?: string): string {
  const values = value as Record<string, string> | undefined;
  return values?.[locale] || (fallbackLocale && values?.[fallbackLocale]) || "";
}

export type InfoPageAttributes = Record<string, string | number | null | undefined>;

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, char => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;", "'": "&#39;" })[char]);

// Replaces %{key} placeholders in information page HTML with the given (escaped) attribute values.
// Unknown or blank attributes render as an empty string.
export function interpolateInfoPageAttributes(html: string, attributes: InfoPageAttributes): string {
  return html.replace(/%\{(\w+)\}/g, (_match, key: string) => escapeHtml(String(attributes[key] ?? "")));
}
